import type { CMSData } from "@/cms/models";
import { isCmsPreviewApplication } from "@/guide/identity";
import { isLookSystem } from "@/utils/look-gate";

/** Steps from the section's JSON to a node in it, e.g. `["specifics", "rootComponent", "items", 0]`. */
export type JsonPath = (string | number)[];

export interface PreviewSelection {
  path: JsonPath;
  /** the section's palette colour, for a click on its own area */
  colour?: string;
}

/** Only the CMS's section preview of a converted website marks its nodes, so the CMS can select a clicked one. */
export function selectablePreview(
  cmsData: Pick<CMSData, "applicationId" | "theme"> | undefined,
) {
  return (
    isCmsPreviewApplication(cmsData?.applicationId) &&
    isLookSystem(cmsData?.theme)
  );
}

function isJsonPath(value: unknown): value is JsonPath {
  return (
    Array.isArray(value) &&
    value.every(
      (step) =>
        typeof step === "string" || (Number.isInteger(step) && step >= 0),
    )
  );
}

function nodePath(value: string | null) {
  try {
    const path: unknown = JSON.parse(value ?? "");
    return isJsonPath(path) ? path : undefined;
  } catch {
    return undefined;
  }
}

/** The node a click hit, by the nearest `data-re-path`; a click outside every node selects the section itself. */
export function previewSelection(target: unknown): PreviewSelection {
  const element =
    typeof (target as Element | null)?.closest === "function"
      ? (target as Element)
      : undefined;
  const path = nodePath(
    element?.closest("[data-re-path]")?.getAttribute("data-re-path") ?? null,
  );
  if (path) return { path };
  const background = element
    ?.closest("[data-re-bg]")
    ?.getAttribute("data-re-bg");
  return background && !background.startsWith("#")
    ? { path: [], colour: `var(--re-p-${background})` }
    : { path: [] };
}
